import { coreApi } from '$api'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { Menu, MenuGroup, MenuLine } from '$models'

enum QueryKeys {
  GetAllMenus = 'getAllMenus',
  GetMenu = 'getMenu',
}

/**
 * Retrieves all menus
 * @param includeLines whether to include menu lines in the response (defaults to false)
 * @returns a list of menus
 */
export const getAllMenus = async (
  includeLines: boolean = true
): Promise<Menu[]> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return []

  const menuModels = await singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.GetAllMenus, includeLines],
    queryFn: async () => {
      return await coreApi.getMenus(includeLines)
    },
  })

  return menuModels.map((m) => m.toJSON())
}

/**
 * Retrieves a single menu by ID
 * @param menuId the ID of the menu to retrieve
 * @returns the menu or null if not found
 */
export const getMenu = async (menuId: string): Promise<Menu | null> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) return null

  const menuModel = await singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.GetMenu, menuId],
    queryFn: async () => {
      return await coreApi.getMenu(menuId)
    },
  })
  return menuModel ? menuModel.toJSON() : null
}

/**
 * Creates a new menu
 * @param newMenu the new menu to be created
 * @returns the created menu
 */
export const createMenu = async (newMenu: Menu): Promise<Menu> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error("The user doesn't have permission to create a new menu.")
  }

  const menuModel = new Menu({
    ...newMenu,
    groups: newMenu.groups?.map(
      (g) =>
        new MenuGroup({
          ...g,
          lines: g.lines?.map(
            (l) =>
              new MenuLine({
                ...l,
                recipeId: l.recipeId ?? undefined,
                stockItemId: l.stockItemId ?? undefined,
                stockUnitRatio:
                  l.stockUnitRatio == null
                    ? undefined
                    : String(l.stockUnitRatio),
                salesTaxRateId:
                  l.salesTaxRateId == null
                    ? undefined
                    : Number(l.salesTaxRateId),
              })
          ),
        })
    ),
  })

  const createdMenuModel = await coreApi.addMenu(menuModel)
  return createdMenuModel.toJSON()
}

/**
 * Updates an existing menu
 * @param menuId the ID of the menu to update
 * @param updatedMenu the updated menu data
 * @returns void
 */
export const updateMenu = async (
  menuId: string,
  updatedMenu: Menu
): Promise<Menu> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error("The user doesn't have permission to update menus.")
  }

  const menuModel = new Menu({
    ...updatedMenu,
    groups: updatedMenu.groups?.map(
      (g) =>
        new MenuGroup({
          ...g,
          lines: g.lines?.map(
            (l) =>
              new MenuLine({
                ...l,
                recipeId: l.recipeId ?? undefined,
                stockItemId: l.stockItemId ?? undefined,
                stockUnitRatio:
                  l.stockUnitRatio == null
                    ? undefined
                    : String(l.stockUnitRatio),
                salesTaxRateId:
                  l.salesTaxRateId == null
                    ? undefined
                    : Number(l.salesTaxRateId),
              })
          ),
        })
    ),
  })

  const updatedMenuModel = await coreApi.updateMenu(menuId, menuModel)
  return updatedMenuModel.toJSON()
}

/**
 * Deletes a menu by ID
 * @param menuId the ID of the menu to delete
 * @returns void
 */
export const deleteMenu = async (menuId: string): Promise<void> => {
  const { hasStockPermission } = getActiveCompanySettings()

  if (!hasStockPermission) {
    throw new Error("The user doesn't have permission to delete menus.")
  }

  return await coreApi.deleteMenu(menuId)
}
